import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

export default function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const { settings: siteSettings } = useSiteSettings();
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const redirectTo = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect') || '/dashboard';
  })();

  // Check if user is actually logged in; if not, redirect to login
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate('/login');
    });
  }, [navigate]);

  const handleDigitChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const updated = [...codeDigits];
    updated[idx] = digit;
    setCodeDigits(updated);
    if (digit && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleDigitKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const updated = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) updated[i] = text[i];
    setCodeDigits(updated);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
    // Auto-submit if 6 digits pasted
    if (text.length === 6) setTimeout(() => handleVerify(text), 100);
  };

  const handleVerify = async (codeOverride?: string) => {
    const token = codeOverride ?? codeDigits.join('');
    if (token.length !== 6) { setError('Please enter all 6 digits.'); return; }
    setError('');
    setLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate('/login'); return; }

    const res = await fetch(`${SUPABASE_URL}/functions/v1/totp-2fa?action=verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ token }),
    });

    const data = await res.json();
    setLoading(false);

    if (data.error) {
      setAttemptsLeft(prev => Math.max(0, prev - 1));
      setError(data.error);
      setCodeDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }

    // Mark 2FA as verified in session storage so AuthContext knows
    sessionStorage.setItem('2fa_verified', 'true');
    navigate(redirectTo, { replace: true });
  };

  const handleBackupVerify = async () => {
    const cleanCode = backupCode.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    if (!cleanCode) { setError('Enter a backup code.'); return; }
    setError('');
    setLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate('/login'); return; }

    const res = await fetch(`${SUPABASE_URL}/functions/v1/totp-2fa?action=verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ token: cleanCode, isBackup: true }),
    });

    const data = await res.json();
    setLoading(false);

    if (data.error) {
      setError(data.error);
      return;
    }

    sessionStorage.setItem('2fa_verified', 'true');
    navigate(redirectTo, { replace: true });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          {siteSettings.site_logo_url ? (
            <img src={siteSettings.site_logo_url} alt={siteSettings.site_name || 'Logo'} className="h-14 max-w-[200px] mx-auto mb-4 object-contain" />
          ) : (
            <img src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png" alt="API MART" className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover" />
          )}
          <h1 className="text-2xl font-bold text-white">{siteSettings.site_name || 'API MART'}</h1>
        </div>

        <div className="bg-white rounded-2xl p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-brand-500 text-3xl" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h2>
            <p className="text-xs text-gray-400 mt-1">
              {useBackup ? 'Enter one of your backup codes' : 'Enter the 6-digit code from Google Authenticator'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4 flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
              >
                <i className="ri-error-warning-fill text-red-500 mt-0.5 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {!useBackup ? (
            /* TOTP input */
            <div className="space-y-5">
              <div className="flex justify-center gap-2">
                {codeDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => { inputRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleDigitChange(idx, e.target.value)}
                    onKeyDown={e => handleDigitKeyDown(idx, e)}
                    onPaste={handleDigitPaste}
                    autoFocus={idx === 0}
                    className="w-11 h-14 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:outline-none focus:border-brand-400 bg-white transition-colors"
                  />
                ))}
              </div>

              {attemptsLeft < 4 && attemptsLeft > 0 && (
                <div className="text-center text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <i className="ri-error-warning-line mr-1" />{attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} remaining before lockout
                </div>
              )}

              <button
                onClick={() => handleVerify()}
                disabled={loading || codeDigits.join('').length !== 6}
                className="w-full py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <><i className="ri-loader-4-line animate-spin" />Verifying…</> : <><i className="ri-shield-check-line" />Verify Code</>}
              </button>

              <button
                onClick={() => { setUseBackup(true); setError(''); }}
                className="w-full text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors py-1"
              >
                Lost access to your authenticator? Use a backup code
              </button>
            </div>
          ) : (
            /* Backup code input */
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">Backup Code</label>
                <input
                  type="text"
                  value={backupCode}
                  onChange={e => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX"
                  autoFocus
                  className="w-full text-center text-lg font-mono tracking-widest border-2 border-gray-200 rounded-xl px-3 py-3 focus:outline-none focus:border-brand-400 bg-white"
                />
              </div>

              <button
                onClick={handleBackupVerify}
                disabled={loading || !backupCode.trim()}
                className="w-full py-3 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-semibold rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <><i className="ri-loader-4-line animate-spin" />Verifying…</> : <><i className="ri-key-2-line" />Use Backup Code</>}
              </button>

              <button
                onClick={() => { setUseBackup(false); setError(''); setBackupCode(''); }}
                className="w-full text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors py-1"
              >
                Back to authenticator app
              </button>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100 text-center">
            <button
              onClick={handleSignOut}
              className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className="ri-logout-box-line mr-1" />Sign out and use a different account
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
